/**
 * PatientImportDialog — bulk CSV import for patients.
 *
 * Flutter ref: `patient_stack_widget.dart:openImportPatientWidget()`
 * (PM app, `lib/features/menu_container/presentation/widgets/`).
 *
 * UX:
 *   1. Dropzone (drag-and-drop OR click to browse) accepting `.csv`
 *   2. Selected file shows with size + replace/remove
 *   3. Upload button fires `useUploadBulkPatients`
 *   4. While uploading: progress bar (indeterminate — backend doesn't
 *      stream progress events to the React side; Flutter uses a Dio
 *      `onSendProgress` callback that JS `fetch` can't replicate
 *      without `XMLHttpRequest`. Indeterminate matches the visual.)
 *   5. On response:
 *      - `totalcount === "0"` → success toast, close dialog, list
 *        refetches via the hook's onSuccess
 *      - `totalcount !== "0"` → keep dialog open, show error summary +
 *        "Download error report" link to `filepath`
 *
 * Template-download URL (`/Download CSV template` link) is the
 * Azure-Blob URL Flutter hardcodes; preserved verbatim so the
 * template stays in sync across apps without a backend redirect.
 */

import { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { Banner, Button, Modal } from '@scripsteam/scrips-signal-ds';
import { DropFileRow, DropZone } from '@/shared/ui/components/drop-zone';
import { toast } from 'sonner';
import { useUploadBulkPatients } from '@/patients/services/use-upload-bulk-patients';

const TEMPLATE_URL =
  'https://scripsdevstorage.blob.core.windows.net/templatepatientfileupload/1d864e61-aff7-4477-ad3e-1f1cc80b11b0.csv';

/** Client-side cap on imported CSV size — 10 MB. Server has its own
 *  cap; this is the fail-fast guard so a misdrag doesn't POST a huge
 *  file. Headroom for ~25k rows × 200 bytes ≈ 5 MB use cases. */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export interface PatientImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  practiceId: string;
  createdBy: string;
}

export function PatientImportDialog({
  open,
  onOpenChange,
  organizationId,
  practiceId,
  createdBy,
}: PatientImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{ count: string; href: string | null } | null>(null);
  const upload = useUploadBulkPatients();

  const reset = () => {
    setFile(null);
    setErrorInfo(null);
    upload.reset();
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const first = files[0];
    // Accept .csv by extension and known MIME variants. Excel exports
    // sometimes label CSV as `application/vnd.ms-excel`.
    const isCsv =
      first.name.toLowerCase().endsWith('.csv') ||
      first.type === 'text/csv' ||
      first.type === 'application/vnd.ms-excel';
    if (!isCsv) {
      toast.error('Only .csv files are supported.');
      return;
    }
    // Client-side size cap: the server caps at a larger value, but we
    // fail fast on the client so a misclick on a 500 MB file doesn't
    // burn an upload round-trip + the user's time. Samer PR #99 review
    // 2026-05-14. Cap matches the typical CSV-template size budget
    // (~25k rows × 200 bytes ≈ 5 MB) with headroom.
    if (first.size > MAX_FILE_SIZE_BYTES) {
      toast.error(
        `File is ${formatBytes(first.size)} — please keep imports under ${formatBytes(MAX_FILE_SIZE_BYTES)}.`,
      );
      return;
    }
    setFile(first);
    setErrorInfo(null);
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    try {
      const res = await upload.mutateAsync({
        file,
        organizationId,
        practiceId,
        createdBy,
      });
      if (res.totalcount === '0' || res.totalcount == null) {
        toast.success('Import completed — patients added to the list.');
        handleClose(false);
      } else {
        setErrorInfo({ count: res.totalcount, href: res.filepath ?? null });
      }
    } catch {
      // Global error handler surfaces server errors via the QueryClient onError.
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => handleClose(false)}
      title="Import patients"
      description="Upload a CSV file with the patient columns laid out in our template.
            We&apos;ll create one patient per row and report any errors."
      size="md"
      footer={<><Button
            type="button"
            variant="secondary"
            onClick={() => handleClose(false)}
            disabled={upload.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleUpload()}
            disabled={!file || upload.isPending}
          >
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button></>}
    >
        

        {/* The design's import body (patients-list-import-*.html): the template link in body type, the PaDropZone card,
            the chosen-file row, the error Banner. Same handlers and checks as before. */}
        <div data-slot="import-body">
          <a href={TEMPLATE_URL} target="_blank" rel="noreferrer" data-slot="import-template-link">
            Download CSV template
          </a>

          {!file ? (
            <DropZone
              as="label"
              look="card"
              dragging={dragOver}
              title="Drag a .csv file here, or click to browse"
              hint={`Only .csv files. Max 1 file, up to ${formatBytes(MAX_FILE_SIZE_BYTES)}.`}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer.files);
              }}
            >
              <input
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                className="sr-only"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </DropZone>
          ) : (
            <DropFileRow
              name={file.name}
              size={formatBytes(file.size)}
              action={
                <button
                  type="button"
                  onClick={reset}
                  disabled={upload.isPending}
                  aria-label="Remove file"
                  className="icon-action disabled:opacity-50"
                >
                  <X className="size-4" />
                </button>
              }
            />
          )}

          {upload.isPending ? (
            <div className="space-y-1">
              <p className="text-sm text-text-muted">Uploading…</p>
              <div className="h-2 rounded-full bg-grey-100 overflow-hidden">
                <div className="h-full w-2/3 bg-brand-primary animate-pulse" />
              </div>
            </div>
          ) : null}

          {errorInfo ? (
            <Banner
              variant="error"
              title={`${errorInfo.count} row${errorInfo.count === '1' ? '' : 's'} could not be imported`}
            >
              The rows that imported successfully are already saved. Fix the failed rows and try again.{' '}
              {errorInfo.href ? (
                <a href={errorInfo.href} target="_blank" rel="noreferrer">
                  Download error report
                </a>
              ) : null}
            </Banner>
          ) : null}
        </div>

        
      </Modal>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
